import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { searchAPI } from '../lib/api';
import { ORDER_STATUSES, TASK_STATUSES, TASK_PRIORITIES } from '../lib/utils';
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandSeparator,
} from './ui/command';
import {
  ShoppingCart,
  CheckSquare,
  Users,
  Package,
  Search,
  Loader2,
  X,
} from 'lucide-react';

const PRIORITY_ICON = { high: '🔴', medium: '🟡', low: '🟢' };

/* Расширенный маппинг статусов — покрывает все варианты */
const ALL_STATUSES_RU = {
  // Orders
  new: 'Новый',
  in_progress: 'В работе',
  completed: 'Завершён',
  cancelled: 'Отменён',
  // Tasks
  pending: 'Ожидает',
  todo: 'Ожидает',
  done: 'Выполнено',
};

const getStatusLabel = (status, statusMap) => {
  return statusMap?.[status]?.label || ALL_STATUSES_RU[status] || status;
};

export default function SearchDialog({ open, onOpenChange }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);

  // Reset on close
  useEffect(() => {
    if (!open) {
      setQuery('');
      setResults(null);
    }
  }, [open]);

  // Debounced search
  const doSearch = useCallback(async (q) => {
    if (!q || q.length < 2) {
      setResults(null);
      return;
    }
    setLoading(true);
    try {
      const res = await searchAPI.search(q);
      setResults(res.data);
    } catch {
      setResults(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(query), 300);
    return () => clearTimeout(debounceRef.current);
  }, [query, doSearch]);

  const go = (path) => {
    onOpenChange(false);
    navigate(path);
  };

  const hasResults = results &&
    (results.orders.length || results.tasks.length || results.customers.length || results.materials.length);

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <div className="flex items-center border-b px-3 pr-10" cmdk-input-wrapper="">
        {loading ? (
          <Loader2 className="mr-2 h-4 w-4 shrink-0 animate-spin text-muted-foreground" />
        ) : (
          <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
        )}
        <input
          className="flex h-11 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground"
          placeholder="Поиск по заказам, задачам, клиентам, материалам..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          autoFocus
        />
        <kbd className="hidden sm:inline-flex h-5 items-center gap-1 rounded border bg-muted px-1.5 text-[10px] font-medium text-muted-foreground ml-2 cursor-pointer hover:bg-accent"
          onClick={() => onOpenChange(false)}
        >
          ESC
        </kbd>
      </div>

      <CommandList className="max-h-[360px]">
        {query.length >= 2 && !loading && !hasResults && (
          <CommandEmpty>Ничего не найдено</CommandEmpty>
        )}

        {query.length < 2 && !results && (
          <div className="py-8 text-center text-sm text-muted-foreground">
            Введите минимум 2 символа для поиска
          </div>
        )}

        {/* Orders */}
        {results?.orders?.length > 0 && (
          <CommandGroup heading="Заказы">
            {results.orders.map((o) => (
              <CommandItem
                key={o.id}
                value={`order-${o.id}`}
                onSelect={() => go(`/orders/${o.id}`)}
                className="cursor-pointer"
              >
                <ShoppingCart className="h-4 w-4 text-blue-500 shrink-0" />
                <div className="flex-1 min-w-0">
                  <span className="font-medium">{o.order_number_display || `№${o.order_number}`}</span>
                  {o.form_schema_name && (
                    <span className="text-muted-foreground ml-1.5">· {o.form_schema_name}</span>
                  )}
                  {o.customer_name && (
                    <span className="text-muted-foreground ml-1.5">· {o.customer_name}</span>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {o.total != null && (
                    <span className="text-xs font-medium">{o.total.toLocaleString('ru-RU')} ₽</span>
                  )}
                  <span className="text-xs text-muted-foreground">
                    {getStatusLabel(o.status, ORDER_STATUSES)}
                  </span>
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {results?.orders?.length > 0 && (results?.tasks?.length > 0 || results?.customers?.length > 0 || results?.materials?.length > 0) && (
          <CommandSeparator />
        )}

        {/* Tasks */}
        {results?.tasks?.length > 0 && (
          <CommandGroup heading="Задачи">
            {results.tasks.map((t) => (
              <CommandItem
                key={t.id}
                value={`task-${t.id}`}
                onSelect={() => go(`/tasks?open=${t.id}`)}
                className="cursor-pointer"
              >
                <CheckSquare className="h-4 w-4 text-emerald-500 shrink-0" />
                <div className="flex-1 min-w-0 truncate">
                  {PRIORITY_ICON[t.priority] || ''} {t.title}
                </div>
                <span className="text-xs text-muted-foreground shrink-0">
                  {getStatusLabel(t.status, TASK_STATUSES)}
                </span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {results?.tasks?.length > 0 && (results?.customers?.length > 0 || results?.materials?.length > 0) && (
          <CommandSeparator />
        )}

        {/* Customers */}
        {results?.customers?.length > 0 && (
          <CommandGroup heading="Клиенты">
            {results.customers.map((c) => (
              <CommandItem
                key={c.id}
                value={`customer-${c.id}`}
                onSelect={() => go(`/customers?open=${c.id}`)}
                className="cursor-pointer"
              >
                <Users className="h-4 w-4 text-violet-500 shrink-0" />
                <div className="flex-1 min-w-0 truncate">{c.name}</div>
                <span className="text-xs text-muted-foreground shrink-0">
                  {c.phone || c.email || ''}
                </span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {results?.customers?.length > 0 && results?.materials?.length > 0 && (
          <CommandSeparator />
        )}

        {/* Materials */}
        {results?.materials?.length > 0 && (
          <CommandGroup heading="Материалы">
            {results.materials.map((m) => (
              <CommandItem
                key={m.id}
                value={`material-${m.id}`}
                onSelect={() => go(`/materials?open=${m.id}`)}
                className="cursor-pointer"
              >
                <Package className="h-4 w-4 text-amber-500 shrink-0" />
                <div className="flex-1 min-w-0 truncate">{m.name}</div>
                <span className="text-xs text-muted-foreground shrink-0">
                  {m.quantity != null ? `${m.quantity} ${m.unit || 'шт'}` : ''}
                </span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
      </CommandList>
    </CommandDialog>
  );
}
